import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Autocomplete,
  Checkbox,
  IconButton,
  Tooltip,
  Divider,
  Paper,
} from '@mui/material';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { enqueueSnackbar } from 'notistack';
import { getGroupInitData, createGroup, getGroupById, updateGroup } from "../../api's/ManagementApi";
import { getAllClientIdNameByDomainIdApi } from "../../api's/ClientApi";
import { getAllEngagementIdNameByClientIdApi } from "../../api's/EngagementApi";
import { getAllWorkflowIdNameByEngagementIdApi } from "../../api's/WorkflowApi";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const WORKFLOW_SELECT_ALL = { workflow_id: '__SELECT_ALL__', workflow_name: 'Select All' };
const MAX_USER_CHIPS = 4; // Show individual chips up to this count; beyond that show "N users selected"

// Normalize id-name API response [{ id, name }] to dropdown shapes
const toClientOptions = (arr) => (Array.isArray(arr) ? arr : []).map((x) => ({ client_id: x.id, client_name: x.name }));
const toEngagementOptions = (arr) =>
  (Array.isArray(arr) ? arr : []).map((x) => ({ engagement_id: x.id, engagement_name: x.name }));
const toWorkflowOptions = (arr) =>
  (Array.isArray(arr) ? arr : []).map((x) => ({ workflow_id: x.id, workflow_name: x.name }));

const AddGroupDialog = ({ open, onClose, onSave, group: groupToEdit }) => {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initData, setInitData] = useState({ domains: [], roles: [], users: [] });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [scopeEntries, setScopeEntries] = useState([createEmptyScopeEntry()]);

  function createEmptyScopeEntry() {
    return {
      id: Date.now() + Math.random(),
      selectedDomain: null,
      selectedClient: null,
      selectedEngagement: null,
      selectedWorkflows: [],
      clients: [],
      engagements: [],
      workflows: [],
      loadingClients: false,
      loadingEngagements: false,
      loadingWorkflows: false,
    };
  }

  useEffect(() => {
    if (!open) return;
    if (groupToEdit) {
      loadGroupForEdit();
    } else {
      setGroupName('');
      setDescription('');
      setSelectedRoles([]);
      setSelectedUsers([]);
      setScopeEntries([createEmptyScopeEntry()]);
      fetchInitData();
    }
  }, [open, groupToEdit?.groupId]);

  const fetchInitData = async () => {
    setLoading(true);
    try {
      const response = await getGroupInitData();
      if (response.httpStatusCode === 200 && response.result) {
        setInitData({
          domains: response.result.domains || [],
          roles: response.result.roles || [],
          users: response.result.users || [],
        });
      }
    } catch (error) {
      console.error('Error fetching group init data:', error);
      enqueueSnackbar('Failed to load group initialization data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadGroupForEdit = async () => {
    if (!groupToEdit?.groupId) return;
    setLoading(true);
    try {
      const [initResponse, groupResponse] = await Promise.all([getGroupInitData(), getGroupById(groupToEdit.groupId)]);
      const init = initResponse.httpStatusCode === 200 && initResponse.result ? initResponse.result : {};
      const domains = init.domains || [];
      const roles = init.roles || [];
      const users = init.users || [];
      setInitData({ domains, roles, users });

      const result = groupResponse?.result || groupResponse;
      const groupNameVal = result.groupName || '';
      const descriptionVal = result.description || '';
      const roleIds = result.roleIds || [];
      const userIds = result.userIds || [];
      const scopeEntriesFromApi = result.scopeEntries || [];

      setGroupName(groupNameVal);
      setDescription(descriptionVal);
      setSelectedRoles(roles.filter((r) => roleIds.includes(r.roleId || r.role_id)));
      setSelectedUsers(users.filter((u) => userIds.includes(u.userId || u.user_id)));

      if (scopeEntriesFromApi.length === 0) {
        setScopeEntries([createEmptyScopeEntry()]);
        setLoading(false);
        return;
      }

      // Merge scope entries that share same domain, client, engagement → one scope with multiple workflows
      const scopeKey = (se) => `${se.domainId || ''}|${se.clientId || ''}|${se.engagementId ?? ''}`;
      const mergedScopes = new Map();
      scopeEntriesFromApi.forEach((se) => {
        const k = scopeKey(se);
        if (!mergedScopes.has(k)) {
          mergedScopes.set(k, {
            domainId: se.domainId,
            clientId: se.clientId,
            engagementId: se.engagementId,
            workflowIds: [],
          });
        }
        const wfId = se.workflowId ?? se.workflow_id ?? se.workflowIdFk ?? null;
        if (wfId != null) {
          mergedScopes.get(k).workflowIds.push(wfId);
        }
      });
      const mergedList = Array.from(mergedScopes.values());

      const domainOptions = [{ domainId: 'ALL', domainName: 'ALL' }, ...domains];
      const initialEntries = mergedList.map((se) => {
        // If domainId is null/undefined, show ALL in Domain
        const selectedDomain =
          se.domainId == null
            ? domainOptions.find((d) => d.domainId === 'ALL') || null
            : domainOptions.find((d) => d.domainId === se.domainId) || null;
        return {
          ...createEmptyScopeEntry(),
          selectedDomain,
          clients: [],
          engagements: [],
          workflows: [],
        };
      });
      setScopeEntries(initialEntries);

      const hydrated = await Promise.all(
        initialEntries.map(async (entry, index) => {
          const se = mergedList[index];
          const next = { ...entry };

          if (!entry.selectedDomain || entry.selectedDomain.domainId === 'ALL') {
            return next;
          }
          try {
            const clientRes = await getAllClientIdNameByDomainIdApi(entry.selectedDomain.domainId);
            const raw = clientRes?.data ?? clientRes?.data?.data ?? clientRes?.data?.result ?? [];
            next.clients = toClientOptions(raw);
            if (se.clientId) {
              const matchClient = (c) => (c.client_id || c.clientId || c.id) === se.clientId;
              next.selectedClient = next.clients.find(matchClient) || null;
            } else {
              // Only domain present → show ALL in Client
              next.selectedClient = { client_id: 'ALL', client_name: 'ALL' };
            }
          } catch (error) {
            console.error(error);
          }

          if (
            next.selectedClient &&
            next.selectedClient.client_id !== 'ALL' &&
            next.selectedClient.clientId !== 'ALL'
          ) {
            const clientId = next.selectedClient.client_id || next.selectedClient.clientId || next.selectedClient.id;
            try {
              const engRes = await getAllEngagementIdNameByClientIdApi(clientId);
              const raw = engRes?.data ?? engRes?.data?.data ?? engRes?.data?.result ?? [];
              next.engagements = toEngagementOptions(raw);
              if (se.engagementId) {
                const matchEngagement = (e) => (e.engagement_id || e.engagementId || e.id) === se.engagementId;
                next.selectedEngagement = next.engagements.find(matchEngagement) || null;
              } else {
                next.selectedEngagement = { engagement_id: 'ALL', engagement_name: 'ALL' };
              }
            } catch (_) {
              next.engagements = [];
              if (!se.engagementId) {
                next.selectedEngagement = { engagement_id: 'ALL', engagement_name: 'ALL' };
              }
            }
          }

          const engIdVal =
            next.selectedEngagement?.engagement_id ??
            next.selectedEngagement?.engagementId ??
            next.selectedEngagement?.id;
          if (next.selectedEngagement && engIdVal && engIdVal !== 'ALL') {
            try {
              const wfRes = await getAllWorkflowIdNameByEngagementIdApi(engIdVal);
              const raw = wfRes?.data ?? wfRes?.data?.data ?? wfRes?.data?.result ?? [];
              next.workflows = toWorkflowOptions(raw);
              const workflowIds = Array.isArray(se.workflowIds) ? se.workflowIds : [];
              if (workflowIds.length > 0) {
                const idSet = new Set(workflowIds);
                const matchWorkflow = (w) => idSet.has(w.workflow_id ?? w.workflowId ?? w.id ?? null);
                next.selectedWorkflows = next.workflows.filter(matchWorkflow);
              } else {
                // null/empty workflow IDs from backend means "Select All"
                next.selectedWorkflows = [...(next.workflows || [])];
              }
            } catch (error) {
              console.error(error);
            }
          }

          return next;
        })
      );
      setScopeEntries(hydrated);
    } catch (error) {
      console.error('Error loading group for edit:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Failed to load group details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDomainChange = async (index, domain) => {
    const updated = [...scopeEntries];
    updated[index] = {
      ...updated[index],
      selectedDomain: domain,
      selectedClient: null,
      selectedEngagement: null,
      selectedWorkflows: [],
      clients: [],
      engagements: [],
      workflows: [],
    };
    setScopeEntries(updated);

    if (domain && domain.domainId !== 'ALL') {
      updated[index].loadingClients = true;
      setScopeEntries([...updated]);
      try {
        const response = await getAllClientIdNameByDomainIdApi(domain.domainId);
        const raw = response?.data ?? response?.data?.data ?? response?.data?.result ?? [];
        updated[index].clients = toClientOptions(raw);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        updated[index].loadingClients = false;
        setScopeEntries([...updated]);
      }
    }
  };

  const handleClientChange = async (index, client) => {
    const updated = [...scopeEntries];
    updated[index] = {
      ...updated[index],
      selectedClient: client,
      selectedEngagement: null,
      selectedWorkflows: [],
      engagements: [],
      workflows: [],
    };
    setScopeEntries(updated);

    if (client && (client.client_id || client.id) !== 'ALL') {
      updated[index].loadingEngagements = true;
      setScopeEntries([...updated]);
      try {
        const clientId = client.client_id || client.clientId || client.id;
        const response = await getAllEngagementIdNameByClientIdApi(clientId);
        const raw = response?.data ?? response?.data?.data ?? response?.data?.result ?? [];
        updated[index].engagements = toEngagementOptions(raw);
      } catch (error) {
        console.error('Error fetching engagements:', error);
      } finally {
        updated[index].loadingEngagements = false;
        setScopeEntries([...updated]);
      }
    }
  };

  const handleEngagementChange = async (index, engagement) => {
    const updated = [...scopeEntries];
    updated[index] = {
      ...updated[index],
      selectedEngagement: engagement,
      selectedWorkflows: [],
      workflows: [],
    };
    setScopeEntries(updated);

    if (engagement && (engagement.engagement_id || engagement.id) !== 'ALL') {
      updated[index].loadingWorkflows = true;
      setScopeEntries([...updated]);
      try {
        const engagementId = engagement.engagement_id || engagement.engagementId || engagement.id;
        const response = await getAllWorkflowIdNameByEngagementIdApi(engagementId);
        const raw = response?.data ?? response?.data?.data ?? response?.data?.result ?? [];
        updated[index].workflows = toWorkflowOptions(raw);
      } catch (error) {
        console.error('Error fetching workflows:', error);
      } finally {
        updated[index].loadingWorkflows = false;
        setScopeEntries([...updated]);
      }
    }
  };

  const handleWorkflowChange = (index, val) => {
    const entry = scopeEntries[index];
    const workflowsList = entry.workflows || [];

    const isSelectAllOption = (o) => o.workflow_id === WORKFLOW_SELECT_ALL.workflow_id;
    const hasSelectAll = val.some(isSelectAllOption);
    const realWorkflows = val.filter((o) => !isSelectAllOption(o));

    const updated = [...scopeEntries];

    if (hasSelectAll) {
      // User clicked Select All → select all workflows.
      // Exception: if they had all selected and then unchecked one/more (realWorkflows has fewer items),
      // treat as partial selection and drop Select All.
      const allCount = workflowsList.length;
      const isPartialDeselect = realWorkflows.length > 0 && realWorkflows.length < allCount;
      if (isPartialDeselect) {
        updated[index].selectedWorkflows = realWorkflows;
      } else {
        updated[index].selectedWorkflows = [...workflowsList];
      }
    } else {
      const hadAllSelected = entry.selectedWorkflows.length === workflowsList.length && workflowsList.length > 0;

      // If previously all were selected (via Select All) and now none are marked as Select All,
      // treat this as "uncheck Select All" → clear everything. Otherwise, just use the new list.
      if (hadAllSelected && realWorkflows.length === workflowsList.length) {
        updated[index].selectedWorkflows = [];
      } else {
        updated[index].selectedWorkflows = realWorkflows;
      }
    }

    setScopeEntries(updated);
  };

  const addScopeEntry = () => {
    setScopeEntries([createEmptyScopeEntry(), ...scopeEntries]);
  };

  const removeScopeEntry = (index) => {
    if (scopeEntries.length > 1) {
      setScopeEntries(scopeEntries.filter((_, i) => i !== index));
    }
  };

  // Each scope entry must have Domain selected; if Domain is not ALL then Client; if Client is not ALL then Engagement; if Engagement is not ALL then at least one Workflow.
  const isScopeEntryComplete = (entry) => {
    if (!entry?.selectedDomain) return false;
    if (entry.selectedDomain.domainId !== 'ALL') {
      if (!entry.selectedClient) return false;
      if ((entry.selectedClient.client_id || entry.selectedClient.clientId) !== 'ALL') {
        if (!entry.selectedEngagement) return false;
        if ((entry.selectedEngagement.engagement_id || entry.selectedEngagement.engagementId) !== 'ALL') {
          const workflows = entry.workflows || [];
          const selected = entry.selectedWorkflows || [];
          if (workflows.length > 0 && selected.length === 0) return false;
        }
      }
    }
    return true;
  };

  // Group can be created with just name and description; scope/domain is optional.
  const canCreateGroup = !!groupName.trim() && !!description.trim();

  const handleSave = async () => {
    if (!groupName.trim()) {
      enqueueSnackbar('Group name is required', { variant: 'error' });
      return;
    }

    if (!description.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    const validScopes = scopeEntries.filter((entry) => entry.selectedDomain);

    setSaving(true);
    try {
      const scopes = validScopes.map((entry) => {
        const isDomainAll = entry.selectedDomain?.domainId === 'ALL';
        const isClientAll =
          !entry.selectedClient || (entry.selectedClient.client_id || entry.selectedClient.clientId) === 'ALL';
        const isEngagementAll =
          !entry.selectedEngagement ||
          (entry.selectedEngagement.engagement_id || entry.selectedEngagement.engagementId) === 'ALL';
        const workflows = entry.selectedWorkflows || [];
        const workflowList = entry.workflows || [];
        const isWorkflowAll = workflowList.length > 0 && workflows.length === workflowList.length;

        const domainId = isDomainAll ? null : entry.selectedDomain?.domainId ?? null;
        const clientId =
          isDomainAll || isClientAll ? null : entry.selectedClient?.clientId || entry.selectedClient?.client_id || null;
        const engagementId =
          isDomainAll || isClientAll || isEngagementAll
            ? null
            : entry.selectedEngagement?.engagementId || entry.selectedEngagement?.engagement_id || null;

        if (workflowList.length > 0 && !isEngagementAll) {
          if (isWorkflowAll) {
            return [{ domainId, clientId, engagementId, workflowId: null }];
          }
          return workflows.map((wf) => ({
            domainId,
            clientId,
            engagementId,
            workflowId: wf.workflow_id || wf.workflowId,
          }));
        }

        return [{ domainId, clientId, engagementId, workflowId: null }];
      });

      const flatScopes = scopes.flat();

      const payload = {
        name: groupName.trim(),
        description: description.trim(),
        roleIds: selectedRoles.map((r) => r.roleId || r.role_id),
        userIds: selectedUsers.map((u) => u.userId || u.user_id),
        scopes: flatScopes,
      };

      if (groupToEdit?.groupId) {
        await updateGroup(groupToEdit.groupId, payload);
        enqueueSnackbar('Group updated successfully', { variant: 'success' });
      } else {
        await createGroup(payload);
        enqueueSnackbar('Group created successfully', { variant: 'success' });
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving group:', error);
      if (error?.response?.status === 400) {
        enqueueSnackbar(error?.response?.data?.error, { variant: 'error' });
      } else if (error?.response?.status === 409) {
        enqueueSnackbar(error?.response?.data?.message || 'A group with the same name already exists', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.message || (groupToEdit ? 'Failed to update group' : 'Failed to create group'),
          { variant: 'error' }
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const domainOptions = [{ domainId: 'ALL', domainName: 'ALL' }, ...initData.domains];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '22px', mb: 0.5 }}>
          {groupToEdit ? 'Edit Group' : 'Add Group'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
          {groupToEdit
            ? 'Update group details, role assignments, and hierarchical scope access.'
            : 'Create a new group with role assignments and hierarchical scope access.'}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} thickness={3.5} sx={{ color: '#00043C' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Group Details */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.75, color: '#333', fontSize: '13px', fontWeight: 600 }}>
                  Group Name *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Developer Team"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#00043C' },
                      '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.75, color: '#333', fontSize: '13px', fontWeight: 600 }}>
                  Description *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this group..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#00043C' },
                      '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Roles and Users - side by side */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                <Typography variant="body2" sx={{ mb: 0.75, color: '#333', fontSize: '13px', fontWeight: 600 }}>
                  Assign Roles
                </Typography>
                <Autocomplete
                  multiple
                  options={initData.roles}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.roleName}
                  isOptionEqualToValue={(option, value) =>
                    (option.roleId || option.role_id) === (value.roleId || value.role_id)
                  }
                  value={selectedRoles}
                  onChange={(_, newValue) => setSelectedRoles(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                        size="small"
                        sx={{
                          mr: 1.5,
                          '&.Mui-checked': { color: '#00043C' },
                        }}
                      />
                      <Box sx={{ py: 0.25, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                          {option.roleName}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.roleId || option.role_id}
                        label={option.roleName}
                        size="small"
                        sx={{
                          backgroundColor: '#f0f2ff',
                          color: '#00043C',
                          fontWeight: 600,
                          fontSize: '12px',
                          border: '1px solid #e0e6ff',
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select roles..." />}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: '#fafafa',
                        border: '1px solid #e8e8e8',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        mt: 0.5,
                        '& .MuiAutocomplete-listbox': {
                          backgroundColor: '#fafafa',
                          py: 0,
                          '& .MuiAutocomplete-option': {
                            borderBottom: '1px solid #eee',
                            '&:last-of-type': { borderBottom: 'none' },
                            '&:hover': { backgroundColor: '#f0f2ff' },
                            '&[aria-selected="true"]': { backgroundColor: '#e8eaff' },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#00043C' },
                      '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                <Typography variant="body2" sx={{ mb: 0.75, color: '#333', fontSize: '13px', fontWeight: 600 }}>
                  Assign Users
                </Typography>
                <Autocomplete
                  multiple
                  options={initData.users}
                  disableCloseOnSelect
                  getOptionLabel={(option) => {
                    const name = option.fullName || option.username || '';
                    const email = option.username || option.email || '';
                    return email ? `${name} (${email})` : name;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option.userId || option.user_id) === (value.userId || value.user_id)
                  }
                  value={selectedUsers}
                  onChange={(_, newValue) => setSelectedUsers(newValue)}
                  renderOption={(props, option) => {
                    const name = option.fullName || option.username || '';
                    const email = option.username || option.email || '';
                    return (
                      <li {...props}>
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                            {name}
                          </Typography>
                          {email && (
                            <Typography variant="caption" sx={{ color: '#555', display: 'block' }}>
                              {email}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) => {
                    if (value.length <= MAX_USER_CHIPS) {
                      return value.map((option, index) => {
                        const name = option.fullName || option.username || '';
                        const email = option.username || option.email || '';
                        const label = email ? `${name} (${email})` : name;
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.userId || option.user_id}
                            label={label}
                            size="small"
                            sx={{
                              backgroundColor: '#E8F5E9',
                              color: '#2E7D32',
                              fontWeight: 600,
                              fontSize: '11px',
                              maxWidth: '100%',
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                          />
                        );
                      });
                    }
                    return (
                      <Chip
                        key="users-summary"
                        label={`${value.length} users selected`}
                        size="small"
                        sx={{
                          backgroundColor: '#E8F5E9',
                          color: '#2E7D32',
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      />
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Select users (optional)..." />
                  )}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: '#fafafa',
                        border: '1px solid #e8e8e8',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        mt: 0.5,
                        '& .MuiAutocomplete-listbox': {
                          backgroundColor: '#fafafa',
                          py: 0,
                          '& .MuiAutocomplete-option': {
                            borderBottom: '1px solid #eee',
                            '&:last-of-type': { borderBottom: 'none' },
                            '&:hover': { backgroundColor: '#f0f2ff' },
                            '&[aria-selected="true"]': { backgroundColor: '#e8eaff' },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#00043C' },
                      '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
                    },
                    '& .MuiAutocomplete-tag': { margin: '2px 4px 2px 0' },
                    '& .MuiInputBase-root': { minHeight: 40, maxHeight: 120, alignItems: 'flex-start', pt: 0.75 },
                  }}
                />
              </Box>
            </Box>

            {/* Scope Access */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '12px',
                      color: '#1a1a1a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                    }}
                  >
                    Scope Access
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                    Define hierarchical access: Domain → Client → Engagement → Workflow
                  </Typography>
                </Box>
                <Tooltip title="Add another scope entry">
                  <IconButton size="small" onClick={addScopeEntry} sx={{ color: '#00043C' }}>
                    <MdAdd size={20} />
                  </IconButton>
                </Tooltip>
              </Box>

              {scopeEntries.map((entry, index) => (
                <Paper
                  key={entry.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    border: '1px solid #e8e8e8',
                    borderRadius: '10px',
                    backgroundColor: '#fafafa',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: '#d0d0d0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#00043C', fontSize: '11px' }}>
                      SCOPE ENTRY {index + 1}
                    </Typography>
                    {scopeEntries.length > 1 && (
                      <Tooltip title="Remove this scope entry">
                        <IconButton size="small" onClick={() => removeScopeEntry(index)} sx={{ color: '#d32f2f' }}>
                          <MdDeleteOutline size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Domain */}
                    <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                      <Typography
                        variant="caption"
                        sx={{ mb: 0.5, display: 'block', color: '#555', fontWeight: 600, fontSize: '11px' }}
                      >
                        Domain *
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={domainOptions}
                        getOptionLabel={(option) => option.domainName}
                        value={entry.selectedDomain}
                        onChange={(_, val) => handleDomainChange(index, val)}
                        renderInput={(params) => <TextField {...params} placeholder="Select domain" />}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                      />
                    </Box>

                    {/* Client - show only if a specific domain is selected (not ALL) */}
                    {entry.selectedDomain && entry.selectedDomain.domainId !== 'ALL' && (
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <Typography
                          variant="caption"
                          sx={{ mb: 0.5, display: 'block', color: '#555', fontWeight: 600, fontSize: '11px' }}
                        >
                          Client
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={
                            entry.loadingClients
                              ? []
                              : [{ client_id: 'ALL', client_name: 'ALL' }, ...(entry.clients || [])]
                          }
                          getOptionLabel={(option) => option.client_name}
                          value={entry.selectedClient}
                          onChange={(_, val) => handleClientChange(index, val)}
                          loading={entry.loadingClients}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={entry.loadingClients ? 'Loading clients...' : 'Select client'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {entry.loadingClients && <CircularProgress color="inherit" size={18} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                        />
                      </Box>
                    )}

                    {/* Engagement - show only if a specific client is selected (not ALL) */}
                    {entry.selectedClient && entry.selectedClient.client_id !== 'ALL' && (
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <Typography
                          variant="caption"
                          sx={{ mb: 0.5, display: 'block', color: '#555', fontWeight: 600, fontSize: '11px' }}
                        >
                          Engagement
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={
                            entry.loadingEngagements
                              ? []
                              : [{ engagement_id: 'ALL', engagement_name: 'ALL' }, ...(entry.engagements || [])]
                          }
                          getOptionLabel={(option) => option?.engagement_name ?? ''}
                          isOptionEqualToValue={(a, b) =>
                            (a?.engagement_id || a?.engagementId) === (b?.engagement_id || b?.engagementId)
                          }
                          value={entry.selectedEngagement}
                          onChange={(_, val) => handleEngagementChange(index, val)}
                          loading={entry.loadingEngagements}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={entry.loadingEngagements ? 'Loading engagements...' : 'Select engagement'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {entry.loadingEngagements && <CircularProgress color="inherit" size={18} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                        />
                      </Box>
                    )}

                    {/* Workflow - show only if a specific engagement is selected (not ALL) */}
                    {entry.selectedEngagement && entry.selectedEngagement.engagement_id !== 'ALL' && (
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <Typography
                          variant="caption"
                          sx={{ mb: 0.5, display: 'block', color: '#555', fontWeight: 600, fontSize: '11px' }}
                        >
                          Workflows
                        </Typography>
                        <Autocomplete
                          multiple
                          size="small"
                          options={
                            entry.loadingWorkflows
                              ? []
                              : (entry.workflows || []).length === 0
                              ? []
                              : (entry.workflows || []).length > 1
                              ? [WORKFLOW_SELECT_ALL, ...(entry.workflows || [])]
                              : entry.workflows || []
                          }
                          noOptionsText="No options available"
                          disableCloseOnSelect
                          getOptionLabel={(option) =>
                            option.workflow_id === WORKFLOW_SELECT_ALL.workflow_id
                              ? option.workflow_name
                              : option.workflow_name || option.workflowName || ''
                          }
                          value={
                            (entry.workflows || []).length === 0
                              ? []
                              : (entry.workflows || []).length > 1 &&
                                entry.selectedWorkflows.length === (entry.workflows || []).length
                              ? [WORKFLOW_SELECT_ALL, ...entry.selectedWorkflows]
                              : entry.selectedWorkflows
                          }
                          onChange={(_, val) => handleWorkflowChange(index, val)}
                          loading={entry.loadingWorkflows}
                          isOptionEqualToValue={(option, value) =>
                            (option.workflow_id || option.workflowId) === (value.workflow_id || value.workflowId)
                          }
                          renderOption={(props, option) => {
                            const isSelectAll = option.workflow_id === WORKFLOW_SELECT_ALL.workflow_id;
                            const selected = isSelectAll
                              ? entry.selectedWorkflows.length === (entry.workflows || []).length &&
                                (entry.workflows || []).length > 0
                              : entry.selectedWorkflows.some((w) => w.workflow_id === option.workflow_id);
                            return (
                              <li {...props}>
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  checked={selected}
                                  sx={{ mr: 1, '&.Mui-checked': { color: '#00043C' } }}
                                />
                                {option.workflow_id === WORKFLOW_SELECT_ALL.workflow_id
                                  ? option.workflow_name
                                  : option.workflow_name || option.workflowName || ''}
                              </li>
                            );
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, i) =>
                              option.workflow_id === WORKFLOW_SELECT_ALL.workflow_id ? null : (
                                <Chip
                                  {...getTagProps({ index: i })}
                                  key={option.workflow_id || option.workflowId}
                                  label={option.workflow_name || option.workflowName}
                                  size="small"
                                  sx={{ fontSize: '11px' }}
                                />
                              )
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                entry.loadingWorkflows
                                  ? 'Loading workflows...'
                                  : entry.selectedWorkflows.length > 0
                                  ? ''
                                  : 'Select workflows'
                              }
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {entry.loadingWorkflows && <CircularProgress color="inherit" size={18} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                        />
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5, backgroundColor: '#fafafa' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving || loading}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: '8px',
            color: '#43425D',
            border: '1px solid #43425D',
            '&:hover': {
              borderColor: '#43425D',
              backgroundColor: 'rgba(67, 66, 93, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || !canCreateGroup}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: '8px',
            backgroundColor: '#00043C',
            boxShadow: '0 4px 14px rgba(0, 4, 60, 0.25)',
            '&:hover': {
              backgroundColor: '#000652',
              boxShadow: '0 6px 20px rgba(0, 4, 60, 0.35)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': { background: '#e0e0e0', color: '#999' },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {groupToEdit ? 'Updating...' : 'Creating...'}
            </>
          ) : groupToEdit ? (
            'Update Group'
          ) : (
            'Create Group'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddGroupDialog;
